import { ReactNode, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';

import { getNavAppConfig } from './nav.helpers';
import { NavAppAdminsMenu } from './nav-app-admins-menu';
import { NavAppsMenu } from './nav-apps-menu';
import { NavBackIconLink } from './nav-back-icon-link';
import { NavLogos } from './nav-logos';
import { NavPlatformMenu } from './nav-platform-menu';

import { AppLogo } from '../app-logo/app-logo';
import { CurrentUserAtom } from '../../atoms/current-user.atom';
import { classNames } from '../../helpers/classNames';
import { getMultiClientPlatformConfigForClient } from '../../helpers/multi-client-config.helpers';

import { App } from '../../../common/types/app.types';
import { checkUserHasAnyRole } from '../../../common/helpers/auth.helpers';
import {
  ADMIN_ROLE,
  AIM_ADMIN_ROLE,
  CHAT_ADMIN_ROLE,
  PSF_ADMIN_ROLE,
} from '../../../common/types/roles-permissions.types';

interface Props {
  app: App;
  appLinks?: ReactNode;
  isPlatform?: boolean;
  showBackIcon?: boolean;
  hideAppsIcon?: boolean;
  customLogoUrl?: string;
  customLogo?: string | ReactNode;
}

export const Nav = ({
  app,
  appLinks,
  customLogo,
  customLogoUrl,
  isPlatform = true,
  hideAppsIcon = false,
  showBackIcon = false,
}: Props) => {
  const currentUser = useAtomValue(CurrentUserAtom);
  const { logo, appName, url, appColor } = getNavAppConfig(app);
  const { appName: mainAppName } = getNavAppConfig('platform');

  // NOTE: Below fixes a window resize glitch from @headlessui/react on menu button click
  const [openAppsMenu, setOpenAppsMenu] = useState(false);
  const [openAppsAdminMenu, setOpenAppsAdminMenu] = useState(false);
  const [openPlatformMenu, setOpenPlatformMenu] = useState(false);
  const [enableRightSideMenuTrigger, setEnableRightSideMenuTrigger] = useState(true);
  const [openRightSideMenu, setOpenRightSideMenu] = useState(false);

  const config = getMultiClientPlatformConfigForClient();
  const isAppAdmin = currentUser && checkUserHasAnyRole(currentUser, [ADMIN_ROLE]);
  const isAimAdmin = currentUser && checkUserHasAnyRole(currentUser, [AIM_ADMIN_ROLE]);
  const isChatAdmin = currentUser && checkUserHasAnyRole(currentUser, [CHAT_ADMIN_ROLE]);
  const isPsfAdmin = currentUser && checkUserHasAnyRole(currentUser, [PSF_ADMIN_ROLE]);
  const filteredRoles = currentUser?.rolesAndPermissions?.filter((role) => !!role.roleId) || [];
  const hideAppsIcons = config.topNav?.hideAppsIcon || hideAppsIcon || (filteredRoles.length || 0) <= 0;

  // NOTE: Below fixes a window resize glitch from @headlessui/react on menu button click
  const element = document.documentElement;
  useEffect(() => {
    if (element) {
      if (element.style.overflow === 'hidden') {
        element.style.overflow = 'auto';
      }
      if (element.style.paddingRight) {
        element.style.paddingRight = '0px';
      }
    }
  }, [element?.style?.overflow, element?.style?.paddingRight]);

  const showModuleSpecificAdmin = (appName: App | string, appLinks: ReactNode) => {
    if (appName && appLinks && !isPlatform) {
      if (isAimAdmin || isChatAdmin || isPsfAdmin) {
        return <NavAppAdminsMenu appName={appName} links={appLinks} setOpenMenu={setOpenAppsAdminMenu} />;
      }
    }
    return <></>;
  };

  return (
    <div
      className={classNames(
        'nav-bg fixed top-0 z-40 flex h-[60px] w-full items-center border-b border-gray-300 shadow',
        config.hideMainBarAppIcons ? 'pr-8 pl-4' : 'px-8',
      )}
    >
      {hideAppsIcons ? undefined : <NavAppsMenu setOpenMenu={setOpenAppsMenu} />}
      {showBackIcon && <NavBackIconLink className={hideAppsIcons ? undefined : 'ml-2'} />}
      <NavLogos
        url={url}
        isPlatform={isPlatform}
        customLogo={customLogo}
        customLogoUrl={customLogoUrl}
        logo={
          <AppLogo icon={logo} color={appColor}>
            {appName}
          </AppLogo>
        }
      />
      <div className={classNames('flex grow items-center justify-end')}>
        {showModuleSpecificAdmin(appName, appLinks)}
        <NavPlatformMenu appName={mainAppName} isAppAdmin={isAppAdmin} setOpenMenu={setOpenPlatformMenu} />
      </div>
    </div>
  );
};
